import { ClientBlogsSection } from "@/components/blog-cron/ClientBlogsSection";
import { CardSection, FamousFood } from "@/components/FoodAndDrink";
import {
  ImageViatorSection,
  ViatorSection,
  type LinkType,
} from "@/components/shared";
import { SimpleSectionTitle } from "@/components/SimpleSectionTitle";
import {
  buildExpediaApartmentSearchLink,
  buildExpediaHotelSearchLink,
} from "@/lib/expedia.link.builder";
import type { performViatorProductSearch } from "@/lib/requests/ads/viator";
import type { getItineraryDays } from "@/lib/requests/getItineraryDays";
import type { LocationBreakdownType } from "@/lib/requests/getLocationBreakdown";
import type { ViatorProductType } from "@/lib/schemas/viator";
import { cn } from "@/lib/utils";
import { toSlug } from "@/lib/utils/slugify";
import {
  icons,
  LucideCarFront,
  LucideCloudSun,
  LucideCoins,
  LucideCookingPot,
  LucideCross,
  LucideHome,
  LucideHotel,
  LucideMoonStar,
  LucideMoveRight,
  LucidePersonStanding,
  type LucideIcon,
} from "lucide-react";
import type { CSSProperties } from "react";
import { KeyPhrases } from "./KeyPhrases";
import { LocationContextProvider } from "./LocationContext";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Underline } from "./Underline";

function LocationIntro({
  description,
  city,
  country,
  links,
}: {
  description: string;
  city?: string;
  country: string;
  links: LinkType[];
}) {
  const descriptionSplit = description.split(". ");

  const descriptionStart = descriptionSplit.slice(0, -1).join(". ") + ".";
  const descriptionEnd = descriptionSplit.pop();

  return (
    <div className="mt-16 pb-10 md:pb-12 max-w-screen-xl mx-auto px-4 flex flex-col md:flex-row justify-between gap-16">
      <div>
        <h1 className="text-6xl md:text-7xl tracking-tighter leading-[1.13] md:leading-[1.13] font-semibold">
          Things To Do <br />
          in{" "}
          <span className="relative">
            {city || country}
            <div className="absolute bg-yellow-300/50 bottom-2 left-2 top-5 -inset-x-2 -z-10 rotate-1" />
          </span>
        </h1>
        <div className="flex justify-start">
          <IntroButtons className="lg:hidden" links={links} />
        </div>
        <div className="max-w-screen-md mt-12 pt-8 border-t space-y-4 text-lg font-light text-foreground/80">
          <p>{descriptionStart}</p>
          <p>{descriptionEnd}</p>
        </div>
      </div>
      <div className="shrink-0 hidden lg:block">
        <IntroButtons links={links} />
      </div>
    </div>
  );
}

function DayPlansIntro({
  destination,
  city,
  country,
}: {
  destination: string;
  city: string | undefined;
  country: string;
}) {
  return (
    <div className="mt-20 pb-12 md:pb-14 flex flex-col items-center text-center px-4 border-b">
      <div className="flex items-baseline gap-2 max-w-screen-md">
        <h1 className="text-6xl md:text-[4.3rem] tracking-tighter leading-[1.13] md:leading-[1.13] font-semibold">
          <span className="relative">
            {destination}
            <div className="absolute bg-sky-300/50 bottom-2 left-2 top-5 -inset-x-2 -z-10 rotate-1" />
          </span>{" "}
          <br className="sm:hidden" />
          Day Trips
        </h1>
      </div>
      <p className="max-w-screen-xs mt-6 text-lg font-light text-foreground/80">
        Make the most of your day with our curated plans, packed with{" "}
        {destination}'s hidden gems and unique experiences!
      </p>
      <MoreAboutDestinationButton
        className="mt-10"
        city={city}
        country={country}
      />
    </div>
  );
}

export function DestinationAdventuresIntro({
  city,
  country,
}: {
  city?: string;
  country: string;
}) {
  const destination = city || country;
  return (
    <div className="mt-20 pb-12 md:pb-14 flex flex-col items-center text-center px-4 border-b">
      <div className="flex items-baseline gap-2 max-w-screen-md">
        <h1 className="text-6xl md:text-[4.3rem] tracking-tighter leading-[1.13] md:leading-[1.13] font-semibold">
          Tours <br className="sm:hidden" />
          in{" "}
          <span className="relative">
            {destination}
            <div className="absolute bg-green-300/50 bottom-2 left-2 top-5 -inset-x-2 -z-10 rotate-1" />
          </span>
        </h1>
      </div>
      <p className="max-w-screen-xs mt-6 text-lg font-light text-foreground/80">
        Experience the best of {destination} with these unique tours and
        unforgettable experiences!
      </p>
      <MoreAboutDestinationButton
        className="mt-10"
        city={city}
        country={country}
      />
    </div>
  );
}

export const MoreAboutDestinationButton = ({
  city,
  country,
  className,
  containerClassName,
  style,
}: {
  city?: string;
  country: string;
  className?: string;
  containerClassName?: string;
  style?: CSSProperties;
}) => (
  <div className={cn("flex justify-center", containerClassName)}>
    <Button
      className={cn("rounded-full drop-shadow mx-auto gap-2", className)}
      variant="yellow"
      asChild
    >
      <a
        href={
          city ? `/${toSlug(country)}/${toSlug(city)}` : `/${toSlug(country)}`
        }
        style={style}
      >
        {city || country} Travel Guide <LucideMoveRight />
      </a>
    </Button>
  </div>
);

export const ReturnHomeButton = ({ className }: { className?: string }) => (
  <div className="flex justify-center">
    <Button
      className={cn("rounded-full drop-shadow mx-auto gap-2", className)}
      variant="yellow"
      asChild
    >
      <a href="/">
        Return Home <LucideMoveRight />
      </a>
    </Button>
  </div>
);

export const AdventuresButton = ({
  city,
  country,
  className,
}: {
  city?: string;
  country: string;
  className?: string;
}) => (
  <div className="flex justify-center">
    <Button
      className={cn("rounded-full drop-shadow mx-auto gap-2", className)}
      variant="yellow"
      asChild
    >
      <a
        href={
          (city
            ? `/${toSlug(country)}/${toSlug(city)}`
            : `/${toSlug(country)}`) + "/adventures"
        }
      >
        {city || country} Adventures <LucideMoveRight />
      </a>
    </Button>
  </div>
);

const IntroButtons = ({
  links,
  className,
}: {
  links: LinkType[];
  className?: string;
}) => (
  <div
    className={cn(
      "mt-8 grid grid-cols-2 gap-2 [&>*]:rounded-3xl [&>*]:w-full [&>*]:h-full",
      className,
    )}
  >
    {links.map(({ iconName, label, ...props }) => {
      const Icon = icons[iconName];

      return (
        <Button
          asChild
          className="flex flex-col items-center gap-2 py-6 sm:py-9 sm:px-8"
          variant="shadow"
          key={label}
        >
          <a {...props}>
            <Icon size={24} />
            <p className="text-sm">{label}</p>
          </a>
        </Button>
      );
    })}
  </div>
);

function LocationDayPlanSection(plan: {
  title: string;
  morning: string;
  afternoon: string;
  evening: string;
  dayNumber: number;
  ads: ViatorProductType[];
  isViatorLoading?: boolean;
  className?: string;
}) {
  function Item(item: { title: string; text: string }) {
    return (
      <div className="[&>*]:inline">
        <div className="font-bold capitalize">{item.title}</div>
        <span className="font-bold">:</span>{" "}
        <p className="font-light">{item.text}</p>
      </div>
    );
  }
  return (
    <section
      className={cn("mt-20 md:mt-32 border-b pb-4 md:pb-16", plan.className)}
    >
      <Card className="drop-shadow-none border-none">
        <CardContent className="p-0">
          <div
            className={cn(
              "flex flex-col md:flex-row md:gap-14",
              plan.dayNumber % 2 == 0 && "",
            )}
          >
            <div className="flex-1 text-start">
              <div className="uppercase text-primary tracking-wide text-lg">
                day {plan.dayNumber}
              </div>
              <span className="text-[2.5rem] leading-[1.18] sm:leading-[1.18] tracking-[-0.06em] sm:text-[3.15rem] mt-2 font-semibold">
                {plan.title}
              </span>
            </div>
            <div className="grid max-w-screen-sm gap-10 text-[17px] sm:text-lg py-10 text-foreground/80 md:p-5 pr-2.5">
              <Item title="morning" text={plan.morning} />
              <Item title="afternoon" text={plan.afternoon} />
              <Item title="evening" text={plan.evening} />
            </div>
          </div>
          {(plan.isViatorLoading || (plan.ads && plan.ads.length > 0)) && (
            <ViatorSection
              className="-mx-4 sm:-mx-1 md:mt-5"
              ads={plan.ads}
              loading="eager"
              isLoading={plan.isViatorLoading}
            />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

const countriesWithNativeEnglish = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
];

export const LocationRecipe = ({
  city,
  country,
  locationBreakdown: {
    days,
    description,
    foodAndDrink,
    photographySpots,
    seasonalAdvice,
    outdoorActivities,
    tippingAndPayment,
    localEtiquette,
    nightlife,
    climate,
    safety,
    costsOfLiving,
    keyPhrases,
    prosAndCons,
    walkability,
    healthcare,
    nearbyAirports,
    popularSports,
    traffic,
    demographicGroups,
    nearbyHiking,
  },
  viator,
  lat,
  lng,
  idPrefix = "",
  introLinks,
  isViatorLoading = false,
}: {
  city?: string;
  country: string;
  locationBreakdown: LocationBreakdownType;
  viator: {
    products: Awaited<ReturnType<typeof performViatorProductSearch>>;
  };
  lat: string;
  lng: string;
  idPrefix?: string;
  introLinks: LinkType[];
  isViatorLoading?: boolean;
}) => {
  const destination = city || country;

  const emptySplittedProductsState = [[], [], [], [], [], [], [], []];

  const splittedProducts = viator.products
    ? Array.from({ length: 8 }, (_, i) => {
        const shift = (i * 4) % viator.products.length;
        return [
          ...viator.products.slice(shift),
          ...viator.products.slice(0, shift),
        ];
      })
    : emptySplittedProductsState;

  return (
    <LocationContextProvider lat={lat} lng={lng}>
      <main className="pb-24">
        <LocationIntro
          description={description}
          city={city}
          country={country}
          links={introLinks}
        />
        {isViatorLoading ||
        (splittedProducts[0] && splittedProducts[0].length > 0) ? (
          <div className="max-w-screen-xl mx-auto px-4">
            <ViatorSection
              className="-mx-4 sm:-mx-1"
              ads={splittedProducts[0]}
              loading="eager"
              isLoading={isViatorLoading}
            />
            <AdventuresButton
              className="mt-14 sm:mt-20"
              city={city}
              country={country}
            />
          </div>
        ) : (
          <>
            <Underline className="mx-auto w-full relative sm:top-10 mt-10" />
            <CardSection
              id={idPrefix + "things-to-do"}
              cards={outdoorActivities}
              title="Outdoor Activities"
              subtitle="Outdoor fun you shouldn't miss!"
            />
          </>
        )}
        <div className="max-w-screen-xl mx-auto px-4 mt-20 sm:mt-28 md:mt-32 xl:mt-36 -mb-72 sm:-mb-44 md:-mb-64">
          <div className="pt-4 relative">
            <div
              id={idPrefix + "day-plans"}
              className="absolute -top-24 sm:-top-28"
            />
            <SimpleSectionTitle
              title="Day Trips"
              subtitle="Spend a day exploring around"
              className="border-b pb-4"
            />
            <LocationDayPlanSection
              {...days[0]}
              dayNumber={1}
              ads={splittedProducts[1]}
            />
            <div className="relative min-h-[52rem] sm:min-h-[40rem]">
              <LocationDayPlanSection
                className="pb-0 md:pb-0"
                {...days[1]}
                dayNumber={2}
                ads={splittedProducts[2]}
              />
              <div className="absolute inset-y-0 -inset-x-4 bg-gradient-to-b from-transparent via-background to-background" />
              <div className="absolute bottom-[40%] sm:bottom-1/3 inset-x-0 flex justify-center">
                <Button
                  className="rounded-full drop-shadow gap-2"
                  variant="yellow"
                  asChild
                >
                  <a
                    href={
                      (city
                        ? `/${toSlug(country)}/${toSlug(city)}`
                        : `/${toSlug(country)}`) + "/day-plans"
                    }
                  >
                    {destination} Day Trips <LucideMoveRight />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
        {city && (
          <ClientBlogsSection
            className="relative z-10"
            title={`Articles about ${city}`}
            city={city}
            country={country}
          />
        )}
        <HeroTextSection
          id={idPrefix + "prices"}
          title="Prices"
          subtitle="Cost Guide"
          text={
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full border-collapse text-gray-800">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-6 text-left font-medium text-gray-700">
                      Item
                    </th>
                    <th className="py-4 px-6 text-right font-medium text-gray-700">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {costsOfLiving.map(({ emoji, name, price }) => (
                    <tr key={name} className="border-b border-gray-100 ">
                      <td className="py-4 px-6">
                        <span className="flex items-center gap-2">
                          <span>{emoji}</span>
                          <span>{name}</span>
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right font-black">
                        {price}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
          Icon={LucideCoins}
          backgroundImage="/prices.avif"
        />
        <section className="py-4 md:py-12 lg:py-14 relative mt-8 md:mt-20 lg:mt-28">
          <div
            id={idPrefix + "highlights"}
            className="absolute -top-16 sm:-top-16"
          />
          <div className="container p-4 md:p-12">
            <SimpleSectionTitle title="Strengths and Weaknesses" subtitle="" />
            <div className="mt-16 grid grid-cols-1 gap-12 md:gap-16 md:grid-cols-2 text-lg font-light">
              <ul className="mt-10 list-decimal list-inside 2xl:list-outside space-y-8">
                {prosAndCons.pros.map((label) => (
                  <li
                    className="marker:font-black marker:text-4xl sm:marker:text-5xl font-medium relative"
                    key={label}
                  >
                    {label}
                    <div className="absolute bg-emerald-300/20 bottom-1.5 left-2 top-1.5 inset-x-8 -z-10 -rotate-2" />
                  </li>
                ))}
              </ul>
              <ul className="mt-10 list-decimal list-inside 2xl:list-outside space-y-8">
                {prosAndCons.cons.map((label) => (
                  <li
                    className="marker:font-black marker:text-4xl sm:marker:text-5xl font-medium relative"
                    key={label}
                  >
                    {label}
                    <div className="absolute bg-red-300/20 bottom-1.5 left-2 top-1.5 inset-x-8 -z-10 -rotate-2" />
                  </li>
                ))}
              </ul>
            </div>
            <Underline className="mx-auto w-full relative sm:top-12 mt-10" />
          </div>
        </section>
        {!countriesWithNativeEnglish.includes(country) && (
          <KeyPhrases keyPhrases={keyPhrases} />
        )}
        {splittedProducts[0] && splittedProducts[0].length > 0 && (
          <CardSection
            id={idPrefix + "things-to-do"}
            cards={outdoorActivities}
            title={`Best Activities in ${destination}`}
            subtitle="Outdoor fun you shouldn't miss!"
          />
        )}
        {isViatorLoading ||
          (splittedProducts[2] && splittedProducts[2].length > 0 && (
            <>
              <ImageViatorSection
                title="Tours"
                subtitle={`Uncover the best of ${destination} with these unforgettable tours.`}
                ads={splittedProducts[2]}
                isLoading={isViatorLoading}
              />
              <AdventuresButton
                className="mt-14 sm:mt-20"
                city={city}
                country={country}
              />
            </>
          ))}
        <CardSection
          id={idPrefix + "attractions"}
          cards={photographySpots}
          title={`Places to Visit in ${destination}`}
          subtitle="Visit one of these nice places."
        />
        {/* Food Section with Background Image */}
        <HeroTextSection
          id={idPrefix + "food"}
          title={`Food in ${destination}`}
          subtitle="Culinary Guide"
          text={
            <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
              {foodAndDrink.intro}
            </div>
          }
          backgroundImage="/food.avif"
          Icon={LucideCookingPot}
        />
        <FamousFood
          id={idPrefix + "famous-food"}
          foodAndDrink={foodAndDrink}
          destination={destination}
        />
        {isViatorLoading ||
          (splittedProducts[3] && splittedProducts[3].length > 0 && (
            <>
              <ImageViatorSection
                title="Unique Experiences"
                subtitle={`Get to know ${destination} better with these unique experiences.`}
                ads={splittedProducts[3]}
                isLoading={isViatorLoading}
              />
              <AdventuresButton
                className="mt-14 sm:mt-20"
                city={city}
                country={country}
              />
            </>
          ))}
        <CardSection
          cards={foodAndDrink.foodMarketsAndStreetFood}
          title="Food Markets"
          subtitle="Explore local food markets"
        />
        <HeroTextSection
          id={idPrefix + "weather"}
          title="Weather"
          subtitle="Climate Guide"
          text={climate}
          backgroundImage="/weather.avif"
          Icon={LucideCloudSun}
          noH2
        />
        <CardSection
          cards={localEtiquette}
          title="How to Behave"
          noH2
          subtitle="Tips on cultural norms and respectful behavior"
        />
        {isViatorLoading ||
          (splittedProducts[4] && splittedProducts[4].length > 0 && (
            <>
              <ImageViatorSection
                title="Hidden Gems"
                subtitle={`Discover the hidden gems of ${destination} with these exclusive activities.`}
                ads={splittedProducts[4]}
                isLoading={isViatorLoading}
              />
              <AdventuresButton
                className="mt-14 sm:mt-20"
                city={city}
                country={country}
              />
            </>
          ))}
        <HeroTextSection
          id={idPrefix + "safety-tips"}
          title="Safety"
          subtitle="Safety Guide"
          text={safety}
          Icon={LucidePersonStanding}
          backgroundImage="/safety.avif"
        />
        <CardSection
          cards={tippingAndPayment}
          title={`Tipping in ${destination}`}
          subtitle="Ensure a smooth experience"
        />
        {isViatorLoading ||
        (splittedProducts[5] && splittedProducts[5].length > 0) ? (
          <>
            <ImageViatorSection
              title={`Top Things To Do`}
              subtitle={`Engage in these activities to dive deeper into ${destination}.`}
              ads={splittedProducts[5]}
              isLoading={isViatorLoading}
            />
            <AdventuresButton
              className="mt-14 sm:mt-20"
              city={city}
              country={country}
            />
          </>
        ) : (
          <div className="w-full overflow-hidden mt-24 sm:mt-36">
            <Underline className="mx-auto w-full scale-x-125 " />
          </div>
        )}
        <CardSection
          cards={seasonalAdvice.map(({ season, description }) => ({
            name: season,
            description,
          }))}
          title="Best Time to Visit"
          subtitle="And what to expect in different seasons..."
        />
        <HeroTextSection
          id={idPrefix + "nightlife"}
          title={`${destination} at Night`}
          subtitle="Nightlife Guide"
          text={nightlife}
          Icon={LucideMoonStar}
          backgroundImage="/nightlife.avif"
        />
        <CardSection
          cards={nearbyHiking}
          title={`Hiking in ${destination}`}
          subtitle="Immerse in an unforgettable hike."
        />
        {isViatorLoading ||
        (splittedProducts[6] && splittedProducts[6].length > 0) ? (
          <>
            <ImageViatorSection
              title="Unforgettable Journeys"
              subtitle={`Delve deeper into ${destination} through these rare and unique activities.`}
              ads={splittedProducts[6]}
              isLoading={isViatorLoading}
            />
            <AdventuresButton
              className="mt-14 sm:mt-20"
              city={city}
              country={country}
            />
          </>
        ) : (
          <div className="w-full overflow-hidden mt-24 sm:mt-36">
            <Underline className="mx-auto w-full scale-x-125 " />
          </div>
        )}
        <CardSection
          cards={nearbyAirports}
          title={city ? "Airports" : "Airports"}
          noH2
          subtitle={`How to reach ${destination} by plane.`}
        />
        <HeroTextSection
          id={idPrefix + "walkability"}
          title="Walkability"
          subtitle="Getting Around"
          text={walkability}
          Icon={LucidePersonStanding}
          backgroundImage="/walkability.avif"
        />
        <CardSection
          cards={demographicGroups}
          title="Demographics"
          noH2
          subtitle="Explore demographics in this area."
        />
        {isViatorLoading ||
        (splittedProducts[7] && splittedProducts[7].length > 0) ? (
          <>
            <ImageViatorSection
              title={`The Soul of ${destination}`}
              noH2
              subtitle={`Discover the essence of ${destination} through these truly unique experiences.`}
              ads={splittedProducts[7]}
              isLoading={isViatorLoading}
            />
            <AdventuresButton
              className="mt-14 sm:mt-20"
              city={city}
              country={country}
            />
          </>
        ) : (
          <div className="w-full overflow-hidden mt-24 sm:mt-36">
            <Underline className="mx-auto w-full scale-x-125 " />
          </div>
        )}
        <CardSection
          cards={popularSports}
          title="Popular Sports"
          noH2
          subtitle="Explore popular sports in region."
        />
        <HeroTextSection
          id={idPrefix + "traffic"}
          title="Traffic"
          subtitle="Transportation"
          text={traffic}
          Icon={LucideCarFront}
          backgroundImage="/traffic.avif"
        />
        {city && <DestinationAccomodations country={country} city={city} />}
        <CardSection
          cards={foodAndDrink.dietaryRestrictions}
          title="Dietary Restrictions"
          noH2
          subtitle="Tips for restricted diets."
        />
        {isViatorLoading ||
        (splittedProducts[8] && splittedProducts[8].length > 0) ? (
          <>
            <ImageViatorSection
              title="Cultural Immersions"
              subtitle={`Experience the heart and soul of ${destination} with these cultural adventures.`}
              ads={splittedProducts[8]}
              isLoading={isViatorLoading}
            />
            <AdventuresButton
              className="mt-14 sm:mt-20"
              city={city}
              country={country}
            />
          </>
        ) : (
          <div className="w-full overflow-hidden mt-24 sm:mt-36">
            <Underline className="mx-auto w-full scale-x-125 " />
          </div>
        )}

        <HeroTextSection
          id={idPrefix + "healthcare"}
          title="Healthcare"
          subtitle="Health & Medical"
          text={healthcare}
          Icon={LucideCross}
          backgroundImage="/healthcare.avif"
        />
        {!city && <DestinationAccomodations country={country} />}
      </main>
    </LocationContextProvider>
  );
};

export const DestinationDayPlans = ({
  city,
  country,
  days,
  viator,
  lat,
  lng,
  isViatorLoading = false,
}: {
  city?: string;
  country: string;
  days: Awaited<ReturnType<typeof getItineraryDays>>;
  viator: {
    products: Awaited<ReturnType<typeof performViatorProductSearch>>;
  };
  lat: string;
  lng: string;
  isViatorLoading?: boolean;
}) => {
  const destination = city || country;

  const emptySplittedProductsState = [[], [], [], [], [], [], [], []];

  const splittedProducts = viator.products
    ? Array.from({ length: 8 }, (_, i) => {
        const shift = (i * 4) % viator.products.length;
        return [
          ...viator.products.slice(shift),
          ...viator.products.slice(0, shift),
        ];
      })
    : emptySplittedProductsState;

  return (
    <LocationContextProvider lat={lat} lng={lng}>
      <main>
        <DayPlansIntro
          destination={destination}
          city={city}
          country={country}
        />
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="pt-4 relative">
            {days.map((day, i) => (
              <LocationDayPlanSection
                key={i}
                {...day}
                dayNumber={i + 1}
                ads={splittedProducts[i]}
                isViatorLoading={isViatorLoading}
              />
            ))}
          </div>
        </div>
      </main>
    </LocationContextProvider>
  );
};

const DestinationAccomodations = ({
  city,
  country,
}: {
  city?: string;
  country: string;
}) => {
  const destination = city || country;

  const hotelsHref = buildExpediaHotelSearchLink({
    destination: city ? `${city},${country}` : country,
  });

  const apartmentsHref = buildExpediaApartmentSearchLink({
    destination: city ? `${city},${country}` : country,
  });

  return (
    <section className="mt-20 md:mt-28 lg:mt-32 xl:mt-48 2xl:mt-44 relative">
      <div id="accommodations" className="absolute -top-28 md:-top-32 " />
      <SimpleSectionTitle
        title="Accomodations"
        subtitle={`Find the best place to stay when visiting ${destination}.`}
      />
      <div className="flex justify-center mt-10">
        <div className="flex gap-2 flex-wrap">
          <Button
            className="rounded-full drop-shadow mx-auto gap-2"
            variant="yellow"
            asChild
          >
            <a
              href={hotelsHref}
              rel="noopener nofollow sponsored"
              target="_blank"
            >
              Hotels <LucideHotel />
            </a>
          </Button>
          <Button
            className="rounded-full drop-shadow mx-auto gap-2"
            variant="yellow"
            asChild
          >
            <a
              href={apartmentsHref}
              rel="noopener nofollow sponsored"
              target="_blank"
            >
              Apartments <LucideHome />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

// Modern Hero-style Text Section Component
const HeroTextSection = ({
  id,
  title,
  subtitle,
  text,
  backgroundImage,
  Icon,
  className,
  noH2,
}: {
  id?: string;
  title: string;
  subtitle?: string;
  text: React.ReactNode;
  backgroundImage?: string;
  Icon?: LucideIcon;
  className?: string;
  noH2?: boolean;
}) => {
  // Always use black theme configuration
  const themeConfig = {
    overlay: "bg-gradient-to-r from-black/80 via-black/60 to-black/40",
    badge: "bg-yellow-400 text-black",
    titleColor: "text-white",
    subtitleColor: "text-white/90",
    iconColor: "text-yellow-300",
    contentBg: "bg-white/95 backdrop-blur-md",
    decorativeColor: "bg-yellow-400/20",
  };

  return (
    <div
      className={cn(
        "relative mt-20 md:mt-28 lg:mt-32 xl:mt-36 2xl:mt-44 min-h-[100vh] flex items-center",
        className,
      )}
      style={{
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Overlay */}
      <div className={cn("absolute inset-0", themeConfig.overlay)} />

      {/* Content */}
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 py-16 md:py-24">
        <div id={id} className="absolute -top-28 md:-top-32" />

        <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Title Section */}
          <div className="space-y-6">
            {subtitle && (
              <div className="inline-block">
                <span
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wide",
                    themeConfig.badge,
                  )}
                >
                  {subtitle}
                </span>
              </div>
            )}
            {Icon && (
              <div className="flex items-center gap-3">
                <Icon
                  className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12",
                    themeConfig.iconColor,
                  )}
                />
              </div>
            )}
            <div
              className={cn(
                "text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight",
                themeConfig.titleColor,
              )}
            >
              {noH2 ? <p>{title}</p> : <h2>{title}</h2>}
            </div>
          </div>

          {/* Content Section */}
          <div className="relative">
            <div
              className={cn(
                "rounded-3xl p-8 md:p-10 shadow-2xl border border-white/20",
                themeConfig.contentBg,
              )}
            >
              <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
                {text}
              </div>
            </div>
            {/* Decorative element */}
            <div
              className={cn(
                "absolute -bottom-4 -right-4 w-24 h-24 rounded-full blur-xl",
                themeConfig.decorativeColor,
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
